import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router";
import { pathOr } from "ramda";
import { v4 as uuidv4 } from "uuid";

import { PageHeading, Pager } from "../../../components";
import CustomersNav from "../../../components/customers-nav";
import GroupItem from "./group-item";
import { getCustomerGroups } from "src/main_app/services/apis";
import { formatNumber } from "src/main_app/utils/common";
import { track } from "src/main_app/mixpanel";
import {
  checkPermission,
  useUserPermissions,
} from "src/main_app/actions";

const LIMIT = 50;

const GroupsPage = () => {
  useEffect(() => {
    track("Customer groups page");
  }, []);

  const history = useHistory();
  const [groups, setGroups] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [isLoaded, setIsLoaded] = useState(false);

  const userPermissions = useUserPermissions();
  const canViewThisPage = useMemo(
    () => checkPermission(userPermissions, "customers-page", "canView"),
    [userPermissions]
  );

  useEffect(() => {
    if (userPermissions.length > 0 && !canViewThisPage) {
      alert("You don't have permission to view this page");
      document.location.href = "/";
    }
  }, [canViewThisPage, userPermissions]);

  const loadGroups = useCallback(
    async (pageNo = 1, searchTerm = search) => {
      setIsLoaded(false);
      try {
        const response = await getCustomerGroups({
          page: pageNo,
          limit: LIMIT,
          ...(searchTerm ? { search: searchTerm } : {}),
        });
        setGroups(pathOr([], ["data", "data"], response));
        setTotal(pathOr(0, ["data", "total"], response));
      } catch (error) {
        console.log("error", error);
        setGroups([]);
        setTotal(0);
      }
      setCurrentPage(pageNo);
      setIsLoaded(true);
    },
    [search]
  );

  useEffect(() => {
    loadGroups(1, "");
  }, []);

  const onSearchSubmit = useCallback(
    (event) => {
      event.preventDefault();
      loadGroups(1, search);
    },
    [loadGroups, search]
  );

  const openGroup = useCallback(
    (group) => history.push(`/customer-group/${group.id}`),
    [history]
  );

  if (userPermissions.length === 0 || !canViewThisPage) return null;

  return (
    <div className="the-content">
      <PageHeading title="Customers" />
      <div className="container-fluid">
        <CustomersNav />
      </div>
      <div className="container-fluid content">
        <div className="action-buttons block-center-between">
          <form className="block-center" onSubmit={onSearchSubmit}>
            <input
              type="text"
              className="form-control"
              placeholder="Search groups"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              style={{ minWidth: "260px" }}
            />
            <button type="submit" className="btn btn-default ml-2">
              Search
            </button>
          </form>
          <div>
            <span className="mr-3">{formatNumber(total)} groups</span>
          </div>
        </div>

        {!isLoaded ? (
          <div className="no-item-message">Loading groups…</div>
        ) : groups.length === 0 ? (
          <div className="no-item-message">
            No groups yet. Open a customer and tick “This customer is the main
            meter of a multi-unit group” on its Overview tab to create one.
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table customer-list">
                <thead>
                  <tr>
                    <th scope="col">Group</th>
                    <th scope="col">Address</th>
                    <th scope="col">Meter</th>
                    <th scope="col">Units</th>
                    <th scope="col" className="text-center">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map((group) => (
                    <GroupItem
                      key={uuidv4()}
                      group={group}
                      onOpen={openGroup}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            <div className="d-flex justify-content-lg-end pagination-box">
              <Pager
                onPageChange={(pageNo) => loadGroups(pageNo, search)}
                total={total}
                limit={LIMIT}
                currentPage={currentPage}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default GroupsPage;
